import { useFacture } from '../../api/ventes.js';
import { galerieALaction, galerieAModule } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { useMouvement } from '../../api/mouvements.js';
import { useTranslation } from 'react-i18next';
import { useLangue } from '../../composants/useLangue.js';
import { texteEnLangue } from '@artosera/shared';
import type { CibleRattachement } from '@artosera/shared';
import { useArtiste } from '../../api/artistes.js';
import { useContact } from '../../api/contacts.js';
import { useExposition } from '../../api/expositions.js';
import { useOeuvre } from '../../api/oeuvres.js';

export interface RattachementLu {
  readonly type: CibleRattachement;
  readonly id: string;
}

export interface RattachementResolu {
  /** `null` tant que la fiche n'a pas répondu (§ 18, aucun libellé côté API). */
  readonly label: string | null;
  readonly chemin: string;
  readonly chargement: boolean;
}

/** Résout une cible ; tous les hooks restent appelés, seule la lecture autorisée est active. */
export function useRattachementResolu(
  galerieSlug: string,
  rattachement: RattachementLu,
): RattachementResolu {
  const langue = useLangue();
  const { t } = useTranslation('regie');
  const { galerie } = useShellContext();
  const avecFactures =
    galerieAModule(galerie, 'facturation') && galerieALaction(galerie, 'invoices:read');
  const facture = useFacture(
    galerieSlug,
    rattachement.type === 'invoice' && avecFactures ? rattachement.id : null,
  );
  const mouvement = useMouvement(
    galerieSlug,
    rattachement.type === 'movement' ? rattachement.id : null,
  );
  const oeuvre = useOeuvre(galerieSlug, rattachement.type === 'artwork' ? rattachement.id : null);
  const artiste = useArtiste(galerieSlug, rattachement.type === 'artist' ? rattachement.id : null);
  const contact = useContact(galerieSlug, rattachement.type === 'contact' ? rattachement.id : null);
  const exposition = useExposition(
    galerieSlug,
    rattachement.type === 'exhibition' ? rattachement.id : null,
  );

  switch (rattachement.type) {
    case 'movement':
      return {
        label: mouvement.data
          ? `${t(`regie:mouvements.types.${mouvement.data.type}`)} · ${mouvement.data.departLe.slice(0, 10)}`
          : null,
        chemin: `/${galerieSlug}/mouvements/${rattachement.id}`,
        chargement: mouvement.isLoading,
      };
    case 'artwork':
      return {
        label: oeuvre.data
          ? `${oeuvre.data.oeuvre.artisteNom} — ${oeuvre.data.oeuvre.titre}`
          : null,
        chemin: `/${galerieSlug}/oeuvres/${rattachement.id}`,
        chargement: oeuvre.isLoading,
      };
    case 'artist':
      return {
        label: artiste.data?.nom ?? null,
        chemin: `/${galerieSlug}/artistes/${rattachement.id}`,
        chargement: artiste.isLoading,
      };
    case 'contact':
      return {
        label: contact.data?.contact.nomAffiche ?? null,
        chemin: `/${galerieSlug}/contacts/${rattachement.id}`,
        chargement: contact.isLoading,
      };
    case 'exhibition':
      return {
        label: exposition.data ? texteEnLangue(exposition.data.exposition.titre, langue) : null,
        chemin: `/${galerieSlug}/expositions/${rattachement.id}`,
        chargement: exposition.isLoading,
      };
    case 'invoice': {
      const lue = avecFactures ? facture.data?.facture : null;
      return {
        label: lue
          ? `${lue.numero ?? t('regie:coffre.rattachements.factureBrouillon')}${lue.contactNom ? ` — ${lue.contactNom}` : ''}`
          : null,
        chemin: `/${galerieSlug}/ventes/${rattachement.id}`,
        chargement: avecFactures && facture.isLoading,
      };
    }
    default:
      return { label: null, chemin: `/${galerieSlug}/coffre`, chargement: false };
  }
}
